import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import ipponApi from "../config/axios";
import { ClientsType } from "../types";
import { toApiErrorPayload } from "@/utils/apiErrorPayload";

const BaseURL = import.meta.env.VITE_API_URL;

export const fetchDetailedClient = createAsyncThunk<any, any>(
  "clients/fetchDetailedClient",
  async (id, { rejectWithValue }) => {
    try {
      const result = await ipponApi.get(`${BaseURL}/api/v1/clients/${id}`);
      return result.data.data;
    } catch (error: unknown) {
      return rejectWithValue(
        toApiErrorPayload(
          error,
          "An error occurred during getting detail of clients"
        )
      );
    }
  }
);

export const updateDetailedClient = createAsyncThunk<any, any>(
  "clients/updateDetailedClient",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const result = await ipponApi.put(
        `${BaseURL}/api/v1/clients/${id}`,
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return result.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data ||
          "An error occurred during updating detail of clients"
      );
    }
  }
);

export const updateFiscalClosingMonth = createAsyncThunk<
  { fiscal_closing_month: number },
  { id: string; fiscal_closing_month: number }
>(
  "clients/updateFiscalClosingMonth",
  async ({ id, fiscal_closing_month }, { rejectWithValue }) => {
    try {
      const result = await ipponApi.patch(
        `${BaseURL}/api/v1/clients/${id}/fiscal-closing-month`,
        { fiscal_closing_month },
      );
      return result.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "決算月の更新に失敗しました",
      );
    }
  },
);

export const fetchClientName = createAsyncThunk<any, any>(
  "clients/fetchClientName",
  async (clientId, { rejectWithValue }) => {
    try {
      const result = await ipponApi.get(
        `${BaseURL}/api/v1/clients/${clientId}/name`
      );
      return result.data.data.client_name;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "An error occurred during getting client name"
      );
    }
  }
);

const initialState: ClientsType = {
  detailed: {
    // factory 互換: data は null 初期化
    data: null,
    loading: false,
    error: false,
    loaded: false,
    isFetching: false,
    isUpdating: false,
  } as any,
  name: {
    data: undefined,
    loading: false,
    error: false,
  },
};

export const clientsSlice = createSlice({
  name: "clients",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchDetailedClient.pending, (state, action) => {
      const nextId =
        typeof action.meta.arg === "string"
          ? action.meta.arg
          : (action.meta.arg as any)?.client_id || null;
      const cur: any = state.detailed;
      // 別 client への遷移時は前データをクリアして race を防ぐ
      if (cur.requestedId !== nextId) {
        state.detailed.data = null;
      }
      cur.requestedId = nextId;
      state.detailed.loading = true;
      state.detailed.isFetching = true;
      state.detailed.error = false;
    });
    builder.addCase(fetchDetailedClient.fulfilled, (state, action) => {
      // race: 古い応答は無視
      const requestedId = (state.detailed as any).requestedId;
      const payloadId = (action.payload as any)?.client_id;
      if (requestedId && payloadId && payloadId !== requestedId) {
        return;
      }
      state.detailed.data = action.payload;
      state.detailed.loading = false;
      state.detailed.isFetching = false;
      state.detailed.error = false;
      state.detailed.loaded = true;
    });
    builder.addCase(fetchDetailedClient.rejected, (state) => {
      state.detailed.loading = false;
      state.detailed.isFetching = false;
      state.detailed.error = true;
    });
    builder.addCase(updateDetailedClient.fulfilled, (state, action) => {
      state.detailed.data = action.payload;
      state.detailed.isUpdating = false;
      state.detailed.error = false;
    });
    builder.addCase(updateFiscalClosingMonth.fulfilled, (state, action) => {
      if (state.detailed.data) {
        (state.detailed.data as any).fiscal_closing_month =
          action.payload.fiscal_closing_month;
      }
    });
    builder.addCase(updateDetailedClient.pending, (state) => {
      state.detailed.isUpdating = true;
      state.detailed.error = false;
    });
    builder.addCase(updateDetailedClient.rejected, (state) => {
      state.detailed.isUpdating = false;
      state.detailed.error = true;
    });
    builder.addCase(fetchClientName.fulfilled, (state, action) => {
      state.name.data = action.payload;
      state.name.loading = false;
      state.name.error = false;
    });
    builder.addCase(fetchClientName.pending, (state) => {
      state.name.loading = true;
      state.name.error = false;
    });
    builder.addCase(fetchClientName.rejected, (state) => {
      state.name.loading = false;
      state.name.error = true;
    });
  },
});

export default clientsSlice.reducer;
